import React, { useState, useEffect } from 'react';
import { X, Share, PlusSquare } from 'lucide-react';

// ── detection helpers ────────────────────────────────────────────────────────

const isIOS = () => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
};

const isSafariOnIOS = () => {
  if (!isIOS()) return false;
  const ua = window.navigator.userAgent;
  // Exclude Chrome (CriOS) and Firefox (FxiOS) on iOS
  return !(/CriOS|FxiOS|OPiOS/.test(ua));
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
};

// ── iOS banner ───────────────────────────────────────────────────────────────

const IOSBanner = ({ onDismiss }) => (
  <div
    data-testid="ios-install-banner"
    className="fixed bottom-20 left-3 right-3 md:left-auto md:right-4 md:w-96 bg-white border border-slate-200 rounded-2xl shadow-xl p-4 z-50"
    style={{ animation: 'slideUp 0.3s ease-out' }}
  >
    <button
      onClick={onDismiss}
      data-testid="ios-banner-dismiss-btn"
      className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
      aria-label="Dismiss"
    >
      <X className="w-4 h-4" />
    </button>

    <div className="flex items-start gap-3">
      <img src="/icon-192.png" alt="App icon" className="w-11 h-11 rounded-xl flex-shrink-0" />
      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm font-semibold text-slate-900 mb-1">Enable Push Notifications</p>
        <p className="text-xs text-slate-500 mb-3 leading-relaxed">
          To receive service notifications, add this app to your Home Screen:
        </p>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-slate-700">
            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-blue-600 text-xs">1</span>
            </div>
            <span>Tap the <Share className="inline w-3.5 h-3.5 text-blue-500 mx-0.5" /> <strong>Share</strong> button in Safari</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-700">
            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-blue-600 text-xs">2</span>
            </div>
            <span>Tap <PlusSquare className="inline w-3.5 h-3.5 text-slate-500 mx-0.5" /> <strong>"Add to Home Screen"</strong></span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-700">
            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-blue-600 text-xs">3</span>
            </div>
            <span>Open the app from your Home Screen and allow notifications</span>
          </div>
        </div>
      </div>
    </div>

    {/* Arrow pointing down to the Safari toolbar */}
    <div
      className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-5 h-5 bg-white border-r border-b border-slate-200 rotate-45"
    />
  </div>
);

// ── Android/Chrome banner ────────────────────────────────────────────────────

const AndroidBanner = ({ onInstall, onDismiss }) => (
  <div
    data-testid="android-install-banner"
    className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white border border-slate-200 rounded-xl shadow-lg p-4 z-50"
    style={{ animation: 'slideUp 0.3s ease-out' }}
  >
    <button
      onClick={onDismiss}
      className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
      aria-label="Close"
    >
      <X className="w-4 h-4" />
    </button>

    <div className="flex items-start gap-3">
      <img src="/icon-192.png" alt="App icon" className="w-12 h-12 rounded-lg" />
      <div className="flex-1">
        <h3 className="font-semibold text-slate-900 mb-1">Install Church Service Planning</h3>
        <p className="text-sm text-slate-600 mb-3">
          Add to your home screen for quick access and push notifications
        </p>
        <button
          onClick={onInstall}
          className="w-full h-10 px-4 rounded-full bg-green-700 text-white hover:bg-green-800 transition-all duration-200 font-medium text-sm"
        >
          Install App
        </button>
      </div>
    </div>
  </div>
);

// ── main component ───────────────────────────────────────────────────────────

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    // iOS Safari: show banner if not already installed and not dismissed
    if (isSafariOnIOS() && !isStandalone()) {
      const dismissed = localStorage.getItem('iosInstallDismissed');
      if (!dismissed) {
        // Delay slightly so it doesn't pop up on first paint
        setTimeout(() => setShowIOS(true), 3000);
      }
    }

    // Android / Chrome: listen for native install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const dismissed = localStorage.getItem('installPromptDismissed');
      if (!dismissed) setShowAndroid(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowAndroid(false);
  };

  const handleAndroidDismiss = () => {
    setShowAndroid(false);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  const handleIOSDismiss = () => {
    setShowIOS(false);
    localStorage.setItem('iosInstallDismissed', 'true');
  };

  if (showIOS) {
    return <IOSBanner onDismiss={handleIOSDismiss} />;
  }
  if (showAndroid) {
    return <AndroidBanner onInstall={handleAndroidInstall} onDismiss={handleAndroidDismiss} />;
  }
  return null;
};

export default InstallPrompt;
